import React, { useState, useEffect, useCallback, useRef } from "react";
import { XCircle, Trash2, BarChart3 } from "lucide-react";
import TableGrid from "modules/swiftboard/components/TableGrid";
import TableToolBar from "modules/swiftboard/components/TableToolBar";
import Pagination from "modules/swiftboard/components/Pagination";
import NoDataState from "modules/swiftboard/components/NoDataState";
import ConfirmDialogue from "components/ui/ConfirmDialogue";
import { useAlert } from "context/AlertContext";
import apiCampaign from "../api";
import { getCampaignColumns } from "./campaignColumns";
import { IN_PROGRESS_STATUSES } from "../features/campaign.constants";
import CampaignWizardModal from "../campaign-wizard/CampaignWizardModal";
import CampaignReport from "../campaign-report/CampaignReport";

const POLL_INTERVAL_MS = 7000;

const CampaignList = ({ onStatsChange }) => {
  const { showAlert } = useAlert();

  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [resetTrigger, setResetTrigger] = useState(0);

  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [reportCampaignId, setReportCampaignId] = useState(null);
  const [confirmData, setConfirmData] = useState({
    isOpen: false, title: "", message: "", confirmText: "Yes", cancelText: "Cancel", icon: null, resolve: null,
  });

  const load = useCallback(async (page, size, q) => {
    setLoading(true);
    try {
      const res = await apiCampaign.getAll({ searchText: q.trim(), limit: size, skip: (page - 1) * size });
      setRecords(res.data?.data || []);
      setTotalCount(res.data?.total_count || 0);
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Failed to load campaigns." });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(1, pageSize, ""); }, [load, pageSize]);

  // Live polling — only while something is in-flight, paused when tab hidden.
  const recordsRef = useRef(records);
  recordsRef.current = records;
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      const hasInFlight = recordsRef.current.some((r) => IN_PROGRESS_STATUSES.includes(r.status));
      if (!hasInFlight) return;
      load(currentPage, pageSize, search);
      onStatsChange?.();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load, currentPage, pageSize, search, onStatsChange]);

  const handleSearch = (val) => {
    setSearch(val);
    setCurrentPage(1);
    load(1, pageSize, val);
  };

  const handleRefresh = () => {
    setSearch("");
    setCurrentPage(1);
    setResetTrigger((k) => k + 1);
    load(1, pageSize, "");
  };

  const handlePageChange = (page, newSize = pageSize) => {
    const s = newSize;
    const p = newSize !== pageSize ? 1 : page;
    setCurrentPage(p);
    setPageSize(s);
    load(p, s, search);
  };

  const handleCreate = () => setIsWizardOpen(true);
  const handleWizardClose = () => setIsWizardOpen(false);
  const handleWizardSuccess = () => {
    load(1, pageSize, search);
    onStatsChange?.();
  };

  const openConfirm = (opts) =>
    new Promise((resolve) => setConfirmData({ ...opts, isOpen: true, resolve }));

  const handleConfirmClose = (result) => {
    confirmData.resolve?.(result);
    setConfirmData((p) => ({ ...p, isOpen: false, resolve: null }));
  };

  const handleCancelCampaign = async (row) => {
    const confirmed = await openConfirm({
      title: "Cancel Campaign",
      message: `Stop sending "${row.campaign_name}"? Recipients already emailed will not be affected.`,
      confirmText: "Cancel Campaign",
      cancelText: "Keep Sending",
      variant: "warning",
      icon: <XCircle size={20} className="text-warning" />,
    });
    if (!confirmed) return;
    try {
      await apiCampaign.cancel(row.id);
      showAlert({ type: "success", message: "Campaign cancelled." });
      load(currentPage, pageSize, search);
      onStatsChange?.();
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Failed to cancel campaign." });
    }
  };

  const handleDeleteCampaign = async (row) => {
    const confirmed = await openConfirm({
      title: "Delete Campaign",
      message: `Are you sure you want to delete "${row.campaign_name}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      variant: "danger",
      icon: <Trash2 size={20} className="text-danger" />,
    });
    if (!confirmed) return;
    try {
      await apiCampaign.delete(row.id);
      showAlert({ type: "success", message: "Campaign deleted successfully." });
      load(currentPage, pageSize, search);
      onStatsChange?.();
    } catch (err) {
      showAlert({ type: "error", message: err.response?.data?.message || "Delete failed." });
    }
  };

  const renderActions = (row) => (
    <div className="flex items-center gap-1.5">
      <button
        onClick={(e) => { e.stopPropagation(); setReportCampaignId(row.id); }}
        className="p-1.5 rounded-lg bg-primary_tint text-primary"
        title="View Report"
      >
        <BarChart3 size={15} />
      </button>
      {IN_PROGRESS_STATUSES.includes(row.status) ? (
        <button
          onClick={(e) => { e.stopPropagation(); handleCancelCampaign(row); }}
          className="p-1.5 rounded-lg bg-warning_bg text-warning"
          title="Cancel"
        >
          <XCircle size={15} />
        </button>
      ) : (
        <button
          onClick={(e) => { e.stopPropagation(); handleDeleteCampaign(row); }}
          className="p-1.5 rounded-lg bg-danger_bg text-danger"
          title="Delete"
        >
          <Trash2 size={15} />
        </button>
      )}
    </div>
  );

  return (
    <div className="space-y-4">
      <TableToolBar
        onSearch={handleSearch}
        onRefresh={handleRefresh}
        onCreate={handleCreate}
        resetSearchTrigger={resetTrigger}
        canCreate
      />

      {!loading && records.length === 0 ? (
        <NoDataState
          icon="file"
          title="No Campaigns Yet"
          message={search ? "No campaigns match your search." : "Create your first campaign to start reaching your audience."}
          showRefresh={false}
          onRefresh={handleRefresh}
          showCreate
          createText="Create New Campaign"
          onCreate={handleCreate}
          size="medium"
        />
      ) : (
        <>
          <div className="overflow-hidden border rounded-lg border-border">
            <TableGrid
              data={records}
              loading={loading}
              columns={getCampaignColumns()}
              currentPage={currentPage}
              pageSize={pageSize}
              showActions
              renderActions={renderActions}
            />
          </div>
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        </>
      )}

      <CampaignWizardModal isOpen={isWizardOpen} onClose={handleWizardClose} onSuccess={handleWizardSuccess} />

      {reportCampaignId && (
        <CampaignReport campaignId={reportCampaignId} onClose={() => setReportCampaignId(null)} />
      )}

      {confirmData.isOpen && (
        <ConfirmDialogue confirmData={confirmData} handleConfirmClose={handleConfirmClose} />
      )}
    </div>
  );
};

export default CampaignList;
